export const CAPTCHA_BOT_STATUS = "CAPTCHA";

/**
 * Statuses a task can be in while a login runs. The live-view session is opened
 * when a login starts and closed the moment it lands - the first login, or a
 * relogin in the middle of the run - so a stopped or finished bot has none.
 */
const LOGIN_CAPABLE_STATUSES = [
  "PENDING",
  "QUEUED",
  "NOT_LOGGED_IN",
  "ECS_CONTAINER_STARTED",
  "TWOFA",
  "2FA",
  CAPTCHA_BOT_STATUS,
  "LOGGED_IN",
  "COLLECTING",
  "PROCESSING",
];

function botCanBeLoggingIn(botWorkStatus?: string | null): boolean {
  const bot = (botWorkStatus || "").toUpperCase();
  return !bot || LOGIN_CAPABLE_STATUSES.includes(bot);
}

/**
 * The operator can open this bot's browser.
 *
 * The bot status is checked as well as the session status: a bot that died
 * mid-login never closed its row, so a row still claiming READY for it is a
 * stale read rather than an open window, and offering the button on it hands
 * out a dead address.
 */
export function isLiveViewReady(
  liveViewStatus?: string | null,
  botWorkStatus?: string | null,
): boolean {
  if ((liveViewStatus || "").toUpperCase() !== "READY") return false;
  return botCanBeLoggingIn(botWorkStatus);
}

/**
 * Why the login running now has no live browser, or "" when that is not the
 * case. The bot writes the reason (every slot busy, a VNC server that did not
 * start) and the row is FAILED only until that login ends.
 */
export function liveViewUnavailableReason(
  liveViewStatus?: string | null,
  liveViewError?: string | null,
  botWorkStatus?: string | null,
): string {
  if ((liveViewStatus || "").toUpperCase() !== "FAILED") return "";
  if (!botCanBeLoggingIn(botWorkStatus)) return "";
  return (liveViewError || "").trim();
}

/**
 * The window should take over the screen by itself: the bot is on a captcha and
 * only a human in its browser can move it on.
 *
 * Driven by the session's own phase rather than by a websocket event. The bot
 * publishes live_view.updated on every change it makes - leasing a slot, going
 * ready, changing phase, closing - so treating the event as "open the window"
 * put it up during the login and again once it was over.
 */
export function isLiveViewNeeded(
  liveViewStatus?: string | null,
  liveViewPhase?: string | null,
  botWorkStatus?: string | null,
): boolean {
  return (
    isLiveViewReady(liveViewStatus, botWorkStatus) &&
    (liveViewPhase || "").toUpperCase() === CAPTCHA_BOT_STATUS
  );
}

/**
 * The window may impose itself only on the operator who started the run.
 * Everyone else keeps the manual "Live browser" button; a row with no
 * initiator (a run from before the field existed, or an API run) opens for
 * nobody.
 */
export function shouldAutoOpenLiveView(
  liveViewStatus: string | null | undefined,
  liveViewPhase: string | null | undefined,
  botWorkStatus: string | null | undefined,
  startedBy: number | null | undefined,
  viewerId: number | null | undefined,
): boolean {
  return (
    startedBy != null &&
    viewerId != null &&
    startedBy === viewerId &&
    isLiveViewNeeded(liveViewStatus, liveViewPhase, botWorkStatus)
  );
}
